import { useState } from 'react'
import { agentSkillsApi } from '../services/agentSkillsApi'
import { agentRefusalFrom } from '../utils/aiAccess'
import { extractErrorMessage } from '../utils/apitools'
import { testChatMessage } from '../utils/skillMarkdown'
import useRouter from './useRouter'
import useSnackbar from './useSnackbar'
import type { SkillDraft } from '../types/agent'

export interface SkillTestRequest {
  skill: { id: string; name: string }
  /** The editor's current form: saved first (with `save`), so the test runs what the user sees. */
  draft: SkillDraft
  /**
   * Write the form to the skill first. True for a draft. False for a live
   * skill: the test runs it as saved, so an untested edit never reaches the
   * people already using it (validator finding, B2).
   */
  save?: boolean
  prompt: string
  /** The jobs the new chat works on: the ones the current chat has. */
  job: string
  jobs: string[]
}

/**
 * "Run a test chat" in the skill editor (B2). It is the user's click, and it
 * spends AI credits like any message: save the form to a draft, start a new
 * chat titled "Skill test: <name>", record it on the skill, seed the chat's
 * first message (install the skill by id, read its SKILL.md, then the user's
 * request) and open it in this tab. The first message rides sessionStorage,
 * which is per tab, so the chat must open here. A test refused once the chat
 * exists deletes the chat, so no empty "Skill test" chat is left behind.
 */
export function useSkillTestRun(customer: string) {
  const router = useRouter()
  const snackbar = useSnackbar()
  const [isRunning, setIsRunning] = useState(false)

  const handleRunTest = async ({ skill, draft, prompt, job, jobs, save = true }: SkillTestRequest) => {
    setIsRunning(true)
    try {
      if (save) await agentSkillsApi.update(skill.id, customer, draft)
      const sessionId = await agentSkillsApi.createTestChat(customer, save ? draft.name : skill.name, job, jobs)
      try {
        await agentSkillsApi.recordTest(skill.id, customer, sessionId)
      } catch (error) {
        // Refused (only the owner may test): the chat made for the test would
        // stay in History empty, so remove it before reporting the refusal.
        await agentSkillsApi.deleteTestChat(sessionId)
        throw error
      }
      sessionStorage.setItem(`agent-initial-payload-${sessionId}`, JSON.stringify({ message: testChatMessage({ id: skill.id, name: save ? draft.name : skill.name }, prompt) }))
      router.navigate('agent-session', { customer, sessionId })
    } catch (error) {
      const response = (error as { response?: { status: number; data: unknown } })?.response
      const refusal = response ? agentRefusalFrom(response.status, response.data) : null
      snackbar.error(refusal?.text ?? extractErrorMessage(error))
    } finally {
      setIsRunning(false)
    }
  }

  return { handleRunTest, isRunning }
}

export default useSkillTestRun
